import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, Bell, UserCheck, Mail, Globe } from 'lucide-react';
import Footer from '../common/Footer';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: Database,
      title: "Information We Collect",
      content: [
        {
          subtitle: "Personal Information",
          items: [
            "Name, email address, and phone number when you create an account",
            "Business information including company name, GST number, and address",
            "Profile picture (optional)",
            "Login credentials (securely encrypted)"
          ]
        },
        {
          subtitle: "Shipment Data",
          items: [
            "Air Waybill (AWB) numbers and Bill of Lading (B/L) numbers",
            "Shipment details: origin, destination, weight, packages",
            "Consignee and exporter information",
            "Shipping Bill dates and customs documentation"
          ]
        },
        {
          subtitle: "Usage Information",
          items: [
            "Device information and browser type",
            "IP address and location data",
            "App usage patterns and preferences",
            "Login history and session data"
          ]
        }
      ]
    },
    {
      icon: Eye,
      title: "How We Use Your Information",
      content: [
        {
          subtitle: "Service Delivery",
          items: [
            "To provide shipment tracking and logistics management services",
            "To send real-time notifications about your shipments",
            "To generate invoices and billing documents",
            "To facilitate communication between parties"
          ]
        },
        {
          subtitle: "Service Improvement",
          items: [
            "To analyze usage patterns and improve our platform",
            "To personalize your experience",
            "To develop new features and services",
            "To ensure platform security and prevent fraud"
          ]
        }
      ]
    },
    {
      icon: Lock,
      title: "Data Security",
      content: [
        {
          subtitle: "Protection Measures",
          items: [
            "Industry-standard SSL/TLS encryption for data transmission",
            "Secure password hashing and storage",
            "Regular security audits and vulnerability assessments",
            "Access controls and authentication mechanisms",
            "Secure cloud infrastructure with AWS"
          ]
        },
        {
          subtitle: "Data Storage",
          items: [
            "Data stored on secure servers in compliance with industry standards",
            "Regular automated backups",
            "Encrypted database storage",
            "Secure file storage using Amazon S3 with CloudFront CDN"
          ]
        }
      ]
    },
    {
      icon: Bell,
      title: "Notifications & Communications",
      content: [
        {
          subtitle: "Push Notifications",
          items: [
            "Real-time shipment status updates",
            "New shipment creation alerts",
            "Invoice and billing notifications",
            "You can opt-out of notifications anytime in app settings"
          ]
        },
        {
          subtitle: "Email Communications",
          items: [
            "Account verification and password reset emails",
            "Important service updates and announcements",
            "Shipment-related communications",
            "You can unsubscribe from marketing emails anytime"
          ]
        }
      ]
    },
    {
      icon: UserCheck,
      title: "Your Rights & Choices",
      content: [
        {
          subtitle: "Access & Control",
          items: [
            "Access your personal data through your profile",
            "Update or correct your information anytime",
            "Request deletion of your account and associated data",
            "Export your shipment data"
          ]
        },
        {
          subtitle: "Consent",
          items: [
            "Withdraw consent for data processing",
            "Opt-out of non-essential communications",
            "Manage notification preferences",
            "Control data sharing preferences"
          ]
        }
      ]
    },
    {
      icon: Globe,
      title: "Data Sharing & Third Parties",
      content: [
        {
          subtitle: "We May Share Data With",
          items: [
            "Service providers who assist in platform operations",
            "Payment processors for billing purposes",
            "Cloud service providers (AWS) for data storage",
            "Analytics services to improve our platform"
          ]
        },
        {
          subtitle: "We Do NOT",
          items: [
            "Sell your personal information to third parties",
            "Share data with advertisers",
            "Disclose information except as required by law"
          ]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen relative flex flex-col overflow-hidden animate-fadeIn">
      {/* Background Image - Same as Login */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/images/Map-2.png')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 via-gray-900/30 to-blue-800/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Main Card - Same as Login */}
          <div className="bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 animate-scaleIn">
            
            {/* Logo & Header */}
            <div className="text-center mb-6 animate-slideDown">
              <Link to="/">
                <img 
                  src="/images/logo.png" 
                  alt="QuickLook Logo" 
                  className="h-24 sm:h-28 md:h-32 w-auto mx-auto drop-shadow-2xl mb-4"
                />
              </Link>
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-2xl mb-4">
                <Shield className="w-6 h-6 text-blue-400" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Privacy Policy</h1>
              <p className="text-gray-400 text-sm">Last Updated: April 16, 2026</p>
            </div>

            {/* Back & Terms Links */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => navigate('/')}
                className="flex items-center text-gray-300 hover:text-white text-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Login
              </button>
              <Link to="/terms" className="text-sm text-blue-400 hover:text-blue-300">
                Terms of Service →
              </Link>
            </div>

            {/* Introduction */}
            <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5 mb-6">
              <p className="text-gray-300 text-sm leading-relaxed">
                QuickLook ("we", "our", or "us") operates the QuickLook web application and mobile application. 
                This Privacy Policy informs you of our policies regarding the collection, use, and disclosure 
                of personal data when you use our Service.
              </p>
            </div>

            {/* Scrollable Content */}
            <div className="max-h-[50vh] overflow-y-auto pr-2 space-y-4 scrollbar-hide">
              {sections.map((section, index) => (
                <div key={index} className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-500/20 rounded-xl">
                      <section.icon className="w-5 h-5 text-blue-400" />
                    </div>
                    <h2 className="text-base sm:text-lg font-bold text-white">
                      {index + 1}. {section.title}
                    </h2>
                  </div>
                  <div className="space-y-4 pl-2">
                    {section.content.map((subsection, subIndex) => (
                      <div key={subIndex}>
                        <h3 className="font-semibold text-gray-200 text-sm mb-2">
                          {subsection.subtitle}
                        </h3>
                        <ul className="space-y-1.5">
                          {subsection.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-2 text-gray-400 text-sm">
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Children's Privacy */}
              <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                <h2 className="text-base sm:text-lg font-bold text-white mb-3">7. Children's Privacy</h2>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Our Service is not intended for use by children under the age of 18. We do not knowingly 
                  collect personally identifiable information from anyone under the age of 18.
                </p>
              </div>

              {/* Data Retention */}
              <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                <h2 className="text-base sm:text-lg font-bold text-white mb-3">8. Data Retention & Deletion</h2>
                <ul className="space-y-1.5 text-gray-400 text-sm mb-3">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Account data: Retained until account deletion is requested</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Shipment records: Retained for 7 years for compliance purposes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Usage logs: Automatically deleted after 90 days</span>
                  </li>
                </ul>
                <div className="bg-slate-800/50 rounded-xl p-3 mt-3">
                  <h3 className="font-semibold text-gray-200 text-sm mb-2">How to Delete Your Account & Data:</h3>
                  <ol className="space-y-1 text-gray-400 text-sm list-decimal list-inside">
                    <li>Go to Profile → Settings in the app</li>
                    <li>Click "Delete Account" option</li>
                    <li>Or email us at <span className="text-blue-400">info@hmrspedition.com</span> with subject "Delete My Account"</li>
                    <li>We will process your request within 30 days</li>
                  </ol>
                </div>
              </div>

              {/* Changes to Policy */}
              <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                <h2 className="text-base sm:text-lg font-bold text-white mb-3">9. Changes to This Policy</h2>
                <p className="text-gray-400 text-sm leading-relaxed">
                  We may update our Privacy Policy from time to time. We will notify you of any changes by 
                  posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
              </div>

              {/* Contact */}
              <div className="bg-blue-500/20 rounded-2xl p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-500/30 rounded-xl">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-white mb-2">10. Contact Us</h2>
                    <div className="space-y-1 text-gray-300 text-sm">
                      <p><strong>Developer:</strong> HMR Spedition</p>
                      <p><strong>Email:</strong> info@hmrspedition.com</p>
                      <p><strong>Website:</strong> https://quicklook.in</p>
                      <p><strong>Address:</strong> New Delhi, India</p>
                    </div>
                    <p className="text-gray-400 text-xs mt-3">
                      For privacy-related queries, data deletion requests, or any concerns about your personal information, 
                      please contact us at the email address above. We aim to respond within 48 hours.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Links */}
            <div className="mt-6 pt-4 border-t border-slate-600/50">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-gray-500 text-xs">
                  © {new Date().getFullYear()} QuickLook. All rights reserved.
                </p>
                <div className="flex items-center gap-4">
                  <Link to="/terms" className="text-xs text-gray-400 hover:text-white">
                    Terms of Service
                  </Link>
                  <span className="text-gray-600">|</span>
                  <span className="text-xs text-blue-400 font-semibold">
                    Privacy Policy
                  </span>
                </div>
              </div>
            </div>

            <Footer tone="dark" className="mt-4" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
